<template>
  <div class="home">
    <!-- ANIMATED BACKGROUND BLOBS -->
    <div class="animated-bg">
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
      <div class="blob blob-3"></div>
    </div>

    <!-- NAVBAR -->
    <nav class="navbar glass" :class="{ 'nav-scrolled': isScrolled }">
      <div class="nav-container">
        <div class="logo" @click="scrollToTop">
          <i class="fas fa-file-pdf"></i>
          <span>the<span class="accent">shiper</span></span>
        </div>
        
        <!-- Desktop Navigation -->
        <div class="nav-links desktop-nav">
          <a href="#problem" @click.prevent="scrollToSection('problem')" class="nav-link" :class="{ active: activeSection === 'problem' }">masalah</a>
          <a href="#solve" @click.prevent="scrollToSection('solve')" class="nav-link" :class="{ active: activeSection === 'solve' }">solusi</a>
        </div>
        
        <router-link to="/dashboard" class="btn-primary-nav">
          <i class="fas fa-arrow-right"></i> login
        </router-link>

        <!-- Mobile Menu Button -->
        <button class="mobile-menu-btn" @click="toggleMobileMenu" aria-label="Menu">
          <i class="fas" :class="mobileMenuOpen ? 'fa-times' : 'fa-bars'"></i>
        </button>
      </div>

      <!-- Mobile Navigation -->
      <div class="mobile-nav" :class="{ 'mobile-nav-open': mobileMenuOpen }">
        <a href="#problem" @click.prevent="handleMobileNavClick('problem')" class="mobile-nav-link">
          <i class="fas fa-exclamation-triangle"></i> masalah
        </a>
        <a href="#solve" @click.prevent="handleMobileNavClick('solve')" class="mobile-nav-link">
          <i class="fas fa-chart-line"></i> solusi
        </a>
        <router-link to="/dashboard" class="mobile-nav-cta" @click="mobileMenuOpen = false">
          <i class="fas fa-arrow-right"></i> login
        </router-link>
      </div>
    </nav>

    <!-- HERO -->
    <section class="hero">
      <div class="hero-inner">
        <div class="hero-badge animated-fade">
          <span class="dot-pulse"></span> 2 jam kerja terbuang?
        </div>
        <h1 class="hero-title animated-slide-up">
          pisahin <span class="gradient-text">100 resi</span><br>
          cuma <span class="counter">8</span>-20 detik
        </h1>
        <p class="hero-desc animated-slide-up-delay">
          scroll cari halaman shopee — tokopedia — lazada satu per satu?<br>
          <strong>selama ini buang-buang waktu.</strong>
        </p>
        <div class="hero-cta animated-slide-up-delay2">
          <router-link to="/dashboard" class="btn-primary-large">
            <i class="fas fa-bolt"></i> coba gratis
            <i class="fas fa-arrow-right arrow-anim"></i>
          </router-link>
          <div class="trust-badge">
            <i class="fas fa-shield-alt"></i> 100% privat — file di browser kamu
          </div>
        </div>
      </div>
      <div class="hero-visual">
        <div class="floating-card float-1">
          <i class="fas fa-hourglass-half"></i>
          <span>3 menit per file</span>
        </div>
        <div class="floating-card float-2">
          <i class="fas fa-file-pdf"></i>
          <span>50 file menumpuk</span>
        </div>
        <div class="floating-card float-3">
          <i class="fas fa-sad-tear"></i>
          <span>mata pedih scroll</span>
        </div>
        <div class="hero-glow"></div>
      </div>
    </section>

    <!-- MASALAH SECTION -->
    <section id="problem" class="problem">
      <div class="container">
        <div class="section-label">
          <i class="fas fa-frown"></i> realita sehari-hari
        </div>
        <h2 class="section-title">ini yang <span class="gradient-text">membosankan</span></h2>
        
        <div class="problem-flow">
          <div class="pain-item" v-for="(pain, i) in pains" :key="pain.title" :style="{ animationDelay: `${i * 0.1}s` }">
            <div class="pain-icon" :style="{ background: pain.grad }">
              <i :class="pain.icon"></i>
            </div>
            <div class="pain-content">
              <h3>{{ pain.title }}</h3>
              <p>{{ pain.desc }}</p>
              <div class="pain-time">
                <i class="fas fa-clock"></i> {{ pain.time }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PERBANDINGAN (tanpa border-left jelek) -->
    <section id="solve" class="solve">
      <div class="container">
        <div class="section-label">
          <i class="fas fa-chart-line"></i> perbandingan
        </div>
        <h2 class="section-title">manual <span class="gradient-text">vs</span> theshiper</h2>
        
        <div class="compare-grid">
          <!-- Cara Lama -->
          <div class="compare-card old">
            <div class="card-glow red"></div>
            <div class="card-header">
              <div class="header-icon">
                <img src="@/assets/cat-old.png" alt="cara lama" class="header-img">
              </div>
              <span class="header-title">cara lama</span>
              <span class="header-badge slow">lambat</span>
            </div>
            <div class="card-body">
              <div class="compare-row">
                <i class="fas fa-times-circle"></i>
                <span>buka satu per satu file pdf</span>
                <i class="fas fa-clock time-icon"></i>
              </div>
              <div class="compare-row">
                <i class="fas fa-times-circle"></i>
                <span>cari halaman marketplace scroll 50x</span>
                <i class="fas fa-clock time-icon"></i>
              </div>
              <div class="compare-row">
                <i class="fas fa-times-circle"></i>
                <span>catat nomor pesanan khusus</span>
                <i class="fas fa-clock time-icon"></i>
              </div>
              <div class="compare-row">
                <i class="fas fa-times-circle"></i>
                <span>gabung ulang di aplikasi lain</span>
                <i class="fas fa-clock time-icon"></i>
              </div>
            </div>
            <div class="card-footer old">
              <i class="fas fa-hourglass-end"></i>
              <span><strong>3-5 menit</strong> per file</span>
            </div>
          </div>

          <!-- theshiper -->
          <div class="compare-card new">
            <div class="card-glow green"></div>
            <div class="card-header">
              <div class="header-icon">
                <img src="@/assets/cat-new.png" alt="theshiper" class="header-img">
              </div>
              <span class="header-title">theshiper</span>
              <span class="header-badge fast">cepat</span>
            </div>
            <div class="card-body">
              <div class="compare-row">
                <i class="fas fa-check-circle"></i>
                <span>upload semua file sekaligus</span>
                <i class="fas fa-check check-icon"></i>
              </div>
              <div class="compare-row">
                <i class="fas fa-check-circle"></i>
                <span>pisah otomatis — keyword marketplace</span>
                <i class="fas fa-check check-icon"></i>
              </div>
              <div class="compare-row">
                <i class="fas fa-check-circle"></i>
                <span>cek halaman langsung di preview</span>
                <i class="fas fa-check check-icon"></i>
              </div>
              <div class="compare-row">
                <i class="fas fa-check-circle"></i>
                <span>download — jadi satu, rapi</span>
                <i class="fas fa-check check-icon"></i>
              </div>
            </div>
            <div class="card-footer new">
              <i class="fas fa-bolt"></i>
              <span><strong>5-10 detik</strong> untuk 100 file!</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA FINAL -->
    <section class="final-cta">
      <div class="cta-glass">
        <div class="cta-sparkle"></div>
        <h2>gak perlu kata-kata lagi.</h2>
        <p>tools ini gratis — langsung pakai. <strong>mau versi pro?</strong> ada, edit multi file sampai ribuan resi bersamaan.</p>
        <router-link to="/dashboard" class="btn-final">
          <i class="fa-brands fa-google"></i> daftar sekarang
          <i class="fas fa-arrow-right"></i>
        </router-link>
        <div class="cta-footnote">
          <i class="fas fa-laptop"></i> <i class="fa-solid fa-mobile-screen"></i> bisa mobile & laptop | tanpa ribet
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <i class="fas fa-file-pdf"></i>
          <span>the<span class="accent">shiper</span></span>
          <p>tools untuk seller marketplace & admin online shop</p>
        </div>
        <div class="footer-social">
          <a href="https://t.me/sellershopid" target="_blank"><i class="fab fa-telegram"></i></a>
          <a href="https://shopee.co.id/sellershopid" target="_blank"><i class="fa-solid fa-bag-shopping"></i></a>
          <a href="https://tiktok.com/@sellershopid" target="_blank"><i class="fab fa-tiktok"></i></a>
          <a href="https://youtube.com/@sellershopid" target="_blank"><i class="fa-brands fa-youtube"></i></a>
        </div>
        <div class="footer-copy">
          © 2026 theshiper v4-r230626 — by sellershopid
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'HomeView',
  data() {
    return {
      pains: [
        {
          icon: 'fas fa-hourglass-half',
          title: 'buang waktu scroll',
          desc: 'cari halaman marketplace di pdf 100+ halaman? mata perih 😭, waktu habis.',
          grad: 'linear-gradient(135deg, #FF6B6B, #FF8E53)',
          time: 'rata-rata 3-5 menit per file'
        },
        {
          icon: 'fas fa-th-large',
          title: 'hasil berantakan',
          desc: 'resi keselip, cek double resi, gabung ulang ribet.',
          grad: 'linear-gradient(135deg, #FFB347, #FF8C00)',
          time: 'gabung ulang bisa 10 menit'
        },
        {
          icon: 'fas fa-layer-group',
          title: 'capek manual',
          desc: 'ulang terus dari awal — padahal caranya sama setiap hari 😩',
          grad: 'linear-gradient(135deg, #6A11CB, #B721FF)',
          time: 'bonus: sakit kepala'
        }
      ],
      mobileMenuOpen: false,
      isScrolled: false,
      activeSection: '',
      isMobile: false
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
    this.updateActiveSection()
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId)
      if (element) {
        const navbarHeight = 80
        const elementPosition = element.offsetTop - navbarHeight
        window.scrollTo({ top: elementPosition, behavior: 'smooth' })
      }
      this.mobileMenuOpen = false
      this.updateActiveSection()
    },
    handleMobileNavClick(sectionId) {
      this.scrollToSection(sectionId)
    },
    toggleMobileMenu() {
      if (!this.isMobile) return
      this.mobileMenuOpen = !this.mobileMenuOpen
      if (this.mobileMenuOpen) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 50
      this.updateActiveSection()
    },
    handleResize() {
      const wasMobile = this.isMobile
      this.isMobile = window.innerWidth <= 768
      
      if (wasMobile && !this.isMobile) {
        this.mobileMenuOpen = false
        document.body.style.overflow = ''
      }
    },
    updateActiveSection() {
      const sections = ['problem', 'solve']
      const scrollPosition = window.scrollY + 100
      
      for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const offsetTop = element.offsetTop
          const offsetBottom = offsetTop + element.offsetHeight
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            this.activeSection = section
            break
          }
        }
      }
    }
  }
}
</script>

<style scoped>
/* ========== LIGHT THEME + GLASSMORPHISM ========== */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.home {
  background: #F5F7FF;
  overflow-x: hidden;
  font-family: 'Inter', 'Noto Sans', -apple-system, sans-serif;
  position: relative;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px;
}

/* ========== ANIMATED BG BLOBS ========== */
.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: floatBlob 20s infinite ease-in-out;
}

.blob-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 129, 255, 0.25), transparent);
  top: -200px;
  left: -200px;
  animation-delay: 0s;
}

.blob-2 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(123, 97, 255, 0.2), transparent);
  bottom: -250px;
  right: -200px;
  animation-delay: 5s;
}

.blob-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(0, 200, 117, 0.15), transparent);
  top: 40%;
  left: 60%;
  animation-delay: 10s;
}

@keyframes floatBlob {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.05); }
  66% { transform: translate(-20px, 40px) scale(0.98); }
}

/* ========== GLASS NAVBAR ========== */
.navbar {
  position: fixed;
  top: 20px;
  left: 0;
  right: 0;
  z-index: 1000;
  margin: 0 24px;
  border-radius: 80px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 129, 255, 0.15);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.navbar.nav-scrolled {
  top: 0;
  margin: 0;
  border-radius: 0;
  background: #FFFFFF;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.nav-container {
  margin: 0 auto;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.logo {
  font-size: 1.3rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.logo:hover {
  transform: scale(1.02);
}

.logo i {
  color: #0081ff;
  font-size: 1.4rem;
}

.logo .accent {
  color: #0081ff;
}

.nav-links {
  display: flex;
  gap: 32px;
}

.nav-link {
  text-decoration: none;
  color: #4A4A6A;
  font-weight: 500;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s;
  position: relative;
  cursor: pointer;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(135deg, #0081ff, #7b61ff);
  transform: scaleX(0);
  transition: transform 0.2s;
}

.nav-link:hover::after,
.nav-link.active::after {
  transform: scaleX(1);
}

.nav-link:hover {
  color: #0081ff;
}

.nav-link.active {
  color: #0081ff;
}

.btn-primary-nav {
  background: linear-gradient(135deg, #0081ff, #3da3ff);
  color: white;
  padding: 8px 20px;
  border-radius: 40px;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.25s;
  box-shadow: 0 4px 12px rgba(0, 129, 255, 0.25);
}

.btn-primary-nav:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 129, 255, 0.35);
}

/* Mobile Menu Button */
.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #4A4A6A;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background 0.2s;
  z-index: 1001;
}

.mobile-menu-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

/* Mobile Navigation */
.mobile-nav {
  position: fixed;
  top: 0;
  right: -100%;
  width: 80%;
  max-width: 320px;
  height: 100vh;
  background: #FFFFFF;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
  padding: 80px 24px 24px;
  transition: right 0.3s ease;
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-nav-open {
  right: 0;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  text-decoration: none;
  color: #4A4A6A;
  font-weight: 500;
  font-size: 1rem;
  border-radius: 12px;
  transition: all 0.2s;
}

.mobile-nav-link i {
  width: 24px;
  color: #0081ff;
}

.mobile-nav-link:hover {
  background: rgba(0, 129, 255, 0.1);
  color: #0081ff;
  transform: translateX(8px);
}

.mobile-nav-cta {
  background: linear-gradient(135deg, #0081ff, #3da3ff);
  color: white;
  padding: 14px 20px;
  border-radius: 40px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  transition: all 0.25s;
}

.mobile-nav-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 129, 255, 0.35);
}

/* RESPONSIVE BREAKPOINTS */
@media (max-width: 768px) {
  .desktop-nav {
    display: none;
  }
  
  .btn-primary-nav {
    display: none;
  }
  
  .mobile-menu-btn {
    display: block;
  }
  
  .navbar {
    margin: 12px;
    top: 12px;
  }
  
  .nav-container {
    padding: 10px 20px;
  }
  
  .hero-title {
    font-size: 2rem !important;
  }
  
  .hero {
    padding: 60px 20px 40px !important;
  }
  
  .container {
    padding: 0 20px;
  }
  
  .section-title {
    font-size: 1.6rem !important;
  }
  
  .compare-card {
    min-width: 280px !important;
    padding: 24px !important;
  }
  
  .cta-glass {
    border-radius: 48px !important;
    padding: 40px 24px !important;
  }
  
  .cta-glass h2 {
    font-size: 1.5rem !important;
  }
}

@media (min-width: 769px) {
  .mobile-nav {
    display: none !important;
  }
  
  .mobile-menu-btn {
    display: none !important;
  }
  
  .desktop-nav {
    display: flex !important;
  }
  
  .btn-primary-nav {
    display: flex !important;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 1.8rem !important;
  }
  
  .pain-item {
    flex-direction: column;
    text-align: center;
    border-radius: 32px;
    padding: 20px;
  }
  
  .compare-card {
    padding: 20px !important;
  }
  
  .compare-row {
    font-size: 0.8rem;
  }
  
  .footer-inner {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
}

/* Overlay untuk mobile menu */
.mobile-nav::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: none;
  z-index: -1;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
}

.mobile-nav-open::before {
  opacity: 1;
  visibility: visible;
}

/* ========== HERO ========== */
.hero {
  min-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 100px 64px 60px;
  position: relative;
  z-index: 2;
  flex-wrap: wrap;
  gap: 40px;
}

.hero-inner {
  flex: 1;
  min-width: 300px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: rgba(0, 129, 255, 0.08);
  backdrop-filter: blur(4px);
  padding: 6px 16px;
  border-radius: 40px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #0081ff;
  margin: 48px 0px 24px 0px;
}

.dot-pulse {
  width: 8px;
  height: 8px;
  background: #FF5733;
  border-radius: 50%;
  display: inline-block;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.2); }
}

.hero-title {
  font-size: 3.8rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 24px;
  color: #1A1A2E;
}

.gradient-text {
  background: linear-gradient(135deg, #0081ff, #7b61ff);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.counter {
  display: inline-block;
  animation: countPop 0.6s ease-out;
}

@keyframes countPop {
  0% { transform: scale(0.5); opacity: 0; }
  80% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}

.hero-desc {
  font-size: 1.1rem;
  color: #6A6A8A;
  margin-bottom: 32px;
  line-height: 1.6;
}

.hero-desc strong {
  color: #FF8C00;
}

.btn-primary-large {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #0081ff, #3da3ff);
  padding: 14px 32px;
  border-radius: 60px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s;
  box-shadow: 0 8px 20px rgba(0, 129, 255, 0.3);
  margin-bottom: 24px;
}

.btn-primary-large:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 30px rgba(0, 129, 255, 0.4);
  gap: 16px;
}

.arrow-anim {
  transition: transform 0.25s;
}

.btn-primary-large:hover .arrow-anim {
  transform: translateX(5px);
}

.trust-badge {
  font-size: 0.75rem;
  color: #00c875;
  display: flex;
  align-items: center;
  gap: 6px;
}

.hero-visual {
  flex: 1;
  min-width: 300px;
  position: relative;
  height: 400px;
}

.floating-card {
  position: absolute;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 60px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #1A1A2E;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.6);
  white-space: nowrap;
}

.float-1 { top: 20%; left: 0; animation: float1 4s infinite ease-in-out; }
.float-2 { top: 50%; right: 0; animation: float2 5s infinite ease-in-out; }
.float-3 { bottom: 10%; left: 20%; animation: float3 3.5s infinite ease-in-out; }

@keyframes float1 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(10px,-15px); } }
@keyframes float2 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-10px,10px); } }
@keyframes float3 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(5px,-8px); } }

.hero-glow {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(0, 129, 255, 0.15), transparent);
  border-radius: 50%;
  filter: blur(40px);
}

/* Animations */
.animated-fade { animation: fadeInUp 0.6s ease-out; }
.animated-slide-up { animation: slideUp 0.6s ease-out 0.1s both; }
.animated-slide-up-delay { animation: slideUp 0.6s ease-out 0.2s both; }
.animated-slide-up-delay2 { animation: slideUp 0.6s ease-out 0.3s both; }

@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
@keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }

/* ========== PROBLEM SECTION ========== */
.problem {
  padding: 80px 0;
  position: relative;
  z-index: 2;
}

.section-label {
  text-align: center;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #0081ff;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.section-title {
  text-align: center;
  font-size: 2.2rem;
  font-weight: 700;
  color: #1A1A2E;
  margin-bottom: 60px;
}

.problem-flow {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 700px;
  margin: 0 auto;
}

.pain-item {
  display: flex;
  align-items: center;
  gap: 24px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  border-radius: 100px;
  padding: 16px 24px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.03);
  animation: slideInLeft 0.5s ease-out both;
  transition: all 0.3s;
}

.pain-item:hover {
  transform: translateX(8px);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

@keyframes slideInLeft {
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
}

.pain-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pain-icon i { font-size: 24px; color: white; }

.pain-content { flex: 1; }
.pain-content h3 { font-size: 1rem; font-weight: 700; margin-bottom: 4px; color: #1A1A2E; }
.pain-content p { font-size: 0.85rem; color: #6A6A8A; margin-bottom: 8px; }
.pain-time { font-size: 0.7rem; color: #FF8C00; display: inline-flex; align-items: center; gap: 6px; }

@media (max-width: 640px) {
  .pain-item { flex-direction: column; text-align: center; border-radius: 32px; padding: 20px; }
}

/* ========== PERBANDINGAN ========== */
.solve {
  padding: 80px 0;
  position: relative;
  z-index: 2;
}

.compare-grid {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 32px;
  flex-wrap: wrap;
}

.compare-card {
  flex: 1;
  min-width: 320px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(16px);
  border-radius: 48px;
  padding: 32px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.compare-card:hover {
  transform: translateY(-8px);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.1);
}

.card-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  opacity: 0;
  transition: opacity 0.5s;
  pointer-events: none;
}

.compare-card:hover .card-glow {
  opacity: 0.1;
}

.card-glow.red { background: radial-gradient(circle, #FF6B6B, transparent 70%); }
.card-glow.green { background: radial-gradient(circle, #00c875, transparent 70%); }

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid rgba(0, 0, 0, 0.05);
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(0, 129, 255, 0.1);
  border-radius: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-img {
  width: 70px;
  height: 70px;
  object-fit: contain;
  border-radius: 20px;
}

.header-title { font-weight: 700; font-size: 1.2rem; color: #1A1A2E; }

.header-badge {
  margin-left: auto;
  padding: 4px 12px;
  border-radius: 40px;
  font-size: 0.7rem;
  font-weight: 600;
}

.header-badge.slow { background: rgba(255, 107, 107, 0.15); color: #FF6B6B; }
.header-badge.fast { background: rgba(0, 200, 117, 0.15); color: #00c875; }

.card-body { margin-bottom: 28px; }

.compare-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  font-size: 0.9rem;
  color: #4A4A6A;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.03);
}

.compare-row .fa-times-circle { color: #FF6B6B; font-size: 1.1rem; width: 22px; }
.compare-row .fa-check-circle { color: #00c875; font-size: 1.1rem; width: 22px; }
.compare-row .time-icon { margin-left: auto; color: #FF8C00; font-size: 0.7rem; opacity: 0.6; }
.compare-row .check-icon { margin-left: auto; color: #00c875; }

.compare-row span { flex: 1; }

.card-footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px solid rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
}

.card-footer.old { color: #FF6B6B; }
.card-footer.new { color: #00c875; }

/* ========== FINAL CTA ========== */
.final-cta {
  padding: 60px 24px 80px;
  position: relative;
  z-index: 2;
}

.cta-glass {
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  border-radius: 80px;
  padding: 56px 40px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.cta-sparkle {
  position: absolute;
  top: -20px;
  left: -20px;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(0, 129, 255, 0.15), transparent);
  border-radius: 50%;
  animation: sparkle 8s infinite;
}

@keyframes sparkle {
  0%,100% { transform: translate(0,0) scale(1); opacity: 0.5; }
  50% { transform: translate(30px,20px) scale(1.2); opacity: 0.8; }
}

.cta-glass h2 { font-size: 2rem; font-weight: 700; margin-bottom: 16px; color: #1A1A2E; }
.cta-glass p { color: #6A6A8A; margin-bottom: 32px; }

.btn-final {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #0081ff, #3da3ff);
  padding: 14px 36px;
  border-radius: 60px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
  box-shadow: 0 8px 20px rgba(0, 129, 255, 0.3);
  margin-bottom: 24px;
}

.btn-final:hover { transform: scale(1.02); gap: 18px; box-shadow: 0 12px 28px rgba(0, 129, 255, 0.4); }
.cta-footnote { font-size: 0.7rem; color: #A0A0C0; display: flex; align-items: center; justify-content: center; gap: 8px; }

/* ========== FOOTER ========== */
.footer {
  background: #FFFFFF;
  padding: 48px 24px 32px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.footer-brand { display: flex; flex-direction: column; gap: 4px; }
.footer-brand i { font-size: 1.4rem; color: #0081ff; }
.footer-brand span { font-weight: 700; font-size: 1rem; }
.footer-brand .accent { color: #0081ff; }
.footer-brand p { font-size: 0.7rem; color: #A0A0C0; }

.footer-social { display: flex; gap: 24px; }
.footer-social a { color: #6A6A8A; font-size: 1.2rem; transition: all 0.2s; }
.footer-social a:hover { color: #0081ff; transform: translateY(-2px); }
.footer-copy { font-size: 0.7rem; color: #C0C0D0; }

@media (max-width: 768px) {
  .footer-inner { flex-direction: column; text-align: center; }
  .hero-title { font-size: 2.4rem; }
  .hero { padding: 40px 24px; }
  .cta-glass { border-radius: 48px; padding: 40px 24px; }
}
</style>